import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@bumphealth/uxui-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faTrashCan,
  faArrowRight,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

import { visibleLines } from '../../services/cartTotals';
import {
  CART_DRAWER,
  reportOfferShown,
} from '../../containers/checkout-unified/analytics';

import ExternalImage from '../product/ExternalImage';
import FreeShippingNudge from './FreeShippingNudge';
import { upsellPricing } from '../../services/upsellAddOns';

const money = (amount) => `$${Number(amount).toFixed(2)}`;

/* Exported because the checkout summary draws the same stepper on its own
   lines. Its stylesheet is already shared for that reason — .coProto-qty is a
   global in this file's stylesheet, not a rule under .coProto-drawer. */
export const Qty = ({ value, onChange, disabled }) => (
  <div className='coProto-qty'>
    <button
      type='button'
      aria-label='Decrease quantity'
      disabled={disabled}
      onClick={() => onChange(value - 1)}
    >
      −
    </button>
    <span>{value}</span>
    <button
      type='button'
      aria-label='Increase quantity'
      disabled={disabled}
      onClick={() => onChange(value + 1)}
    >
      +
    </button>
  </div>
);

/* One row per thing the shopper can add before checking out. The journal and
   the promo add-ons are the same offer as far as this block is concerned: a
   product, a price the promo sets, and a button that puts it on the cart.

   Unstyled for now beyond the block's own class — the shape is what is being
   wired here, the look comes after. */
const Upsell = ({ product, disabled, isAdding, onAdd }) => {
  const { cost, wasPrice } = upsellPricing(product);
  const image = product.media?.[0];

  /* The card is the control, not the button inside it. A button cannot nest in
     a button, and the alternative — a div with a click handler — is reachable
     by mouse only. This way the whole card is one target, keyboard included,
     and ADD is what it looks like rather than what it is. */
  return (
    <button
      type='button'
      className={`coProto-drawer__upsell ${isAdding ? 'is-adding' : ''}`}
      disabled={disabled}
      aria-busy={isAdding || undefined}
      onClick={() => onAdd(product)}
    >
      {image?.url ? (
        <ExternalImage
          src={image.url}
          alt={image.altTag || product.name}
          className='coProto-drawer__upsellThumb'
          sizes='54px'
        />
      ) : (
        <div className='coProto-drawer__upsellThumb' />
      )}
      <div className='coProto-drawer__upsellBody'>
        <span className='coProto-drawer__upsellName'>{product.name}</span>
        <div className='coProto-drawer__upsellFoot'>
          <span className='coProto-drawer__upsellPrice'>
            {wasPrice ? <s>{money(wasPrice)}</s> : null}
            {money(cost)}
          </span>
          {/* One word on every card, not the promo's own buttonText: these sit
              side by side in a row, and each naming its own action made the
              row read as several offers rather than one shelf. */}
          <span className='coProto-drawer__upsellAdd'>
            {isAdding ? 'Adding…' : 'Add'}
          </span>
        </div>
      </div>
    </button>
  );
};

/* Stacked at desktop, still a swiped row on a phone. The card peeking at the
   edge is the only thing saying there are more, and on a phone that is the
   whole layout, so the arrows name the swipe. The stylesheet hides them at
   desktop, which takes them out of the tab order with them. */
const Upsells = ({ upsells, disabled, addingUpsellId, onAdd }) => {
  const rowRef = useRef(null);
  const [scroll, setScroll] = useState({ left: false, right: false });

  const readScroll = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    // Sub-pixel widths leave the far end a fraction short of the arithmetic.
    const left = row.scrollLeft > 1;
    const right = row.scrollWidth - row.clientWidth - row.scrollLeft > 1;
    setScroll((previous) =>
      previous.left === left && previous.right === right
        ? previous
        : { left, right },
    );
  }, []);

  useEffect(() => {
    readScroll();
    window.addEventListener('resize', readScroll);
    return () => window.removeEventListener('resize', readScroll);
  }, [readScroll, upsells.length]);

  const step = (direction) => {
    const row = rowRef.current;
    if (!row) return;
    const first = row.children[0];
    const second = row.children[1];
    /* One card's pitch, its gap included, rather than a viewport: the row
       snaps to cards, and a page lands between two of them. */
    const by = second ? second.offsetLeft - first.offsetLeft : row.clientWidth;
    const stillMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches;
    row.scrollBy({
      left: direction * by,
      behavior: stillMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div className='coProto-drawer__upsells'>
      <div className='coProto-drawer__upsellsHead'>
        <div className='coProto-drawer__upsellsHeadText'>
          <span className='coProto-drawer__upsellsTitle'>Special offer</span>
          {/* The 50% is a fixed claim about data this does not read: cost
              falls back to the shelf price, so what is actually on offer is
              whatever merchandising set. Worth checking against the live
              promos when they change. */}
          <p className='coProto-drawer__upsellsNote'>
            Add now and save up to 50%. Available only with this order.
          </p>
        </div>
        {/* Nothing to scroll is nothing to say: one offer, or the desktop
            stack, leaves no arrows rather than two dead ones. */}
        {scroll.left || scroll.right ? (
          <div className='coProto-drawer__upsellNav'>
            <button
              type='button'
              aria-label='Previous offer'
              disabled={!scroll.left}
              onClick={() => step(-1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              type='button'
              aria-label='Next offer'
              disabled={!scroll.right}
              onClick={() => step(1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        ) : null}
      </div>
      <div
        className='coProto-drawer__upsellRow'
        ref={rowRef}
        onScroll={readScroll}
      >
        {upsells.map((product) => (
          <Upsell
            key={product._id}
            product={product}
            disabled={disabled}
            isAdding={addingUpsellId === product._id}
            onAdd={onAdd}
          />
        ))}
      </div>
    </div>
  );
};

/* Markup and class names follow the drawer's stylesheet, which came across from
   the prototype — the item styles live under .coProto-drawer, so the structure
   has to match or the rows render unstyled. */
const Item = ({ line, onRemove, onQty, disabled }) => (
  <div className='coProto-drawer__item'>
    {line.image ? (
      <ExternalImage
        src={line.image}
        alt={line.name}
        className='coProto-drawer__thumb'
        sizes='64px'
      />
    ) : (
      <div className='coProto-drawer__thumb' />
    )}
    <div className='coProto-drawer__meta'>
      <span className='coProto-drawer__name'>{line.name}</span>
      {line.kind === 'product' ? (
        <Qty
          value={line.qty || 1}
          disabled={disabled}
          onChange={(next) => onQty(line, next)}
        />
      ) : null}
    </div>
    {/* Remove sits at the item's top corner, price beneath it. */}
    <div className='coProto-drawer__aside'>
      <button
        type='button'
        className='coProto-remove'
        disabled={disabled}
        onClick={() => onRemove(line)}
        aria-label={`Remove ${line.name}`}
      >
        <FontAwesomeIcon icon={faTrashCan} />
      </button>
      <span
        className={`coProto-drawer__price ${line.price === 0 ? 'is-free' : ''}`}
      >
        {line.wasPrice ? <s>{money(line.wasPrice)}</s> : null}
        {line.price === 0 ? 'FREE' : money(line.price)}
      </span>
    </div>
  </div>
);

/* Holds the space while the cart is still being read. The drawer opens on the
   click rather than waiting for the round-trip, so the first open has nothing
   to show yet — and "Your cart is empty" would be a claim, not a placeholder. */
const Skeleton = () => (
  <>
    {[0, 1].map((row) => (
      <div className='coProto-drawer__skeleton' key={row}>
        <span className='coProto-drawer__skeletonThumb' />
        <div className='coProto-drawer__skeletonLines'>
          <span />
          <span />
        </div>
      </div>
    ))}
  </>
);

const CartDrawer = ({
  isOpen,
  totals,
  isBusy,
  isAwaitingCart,
  onClose,
  onCheckout,
  onRemoveLine,
  onChangeQty,
  upsells = [],
  onAddUpsell,
  addingUpsellId,
}) => {
  // Mounted a beat before shown, so the panel has a position to slide from.
  const [mounted, setMounted] = useState(isOpen);
  const [shown, setShown] = useState(false);
  const reportedOffer = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => setShown(true)),
      );
      return () => window.cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  /* Reported from here because this is what shows them, and once per opening
     rather than per render: `upsells` is a fresh array every time the provider
     chooses, so the dependency alone would report on every keystroke that
     touches the cart. The ref is what makes it once, and closing is what arms
     it again.

     Not on open alone either — the catalogue read may still be out when the
     drawer opens, and an offer reported before it exists is an impression of
     nothing. */
  useEffect(() => {
    if (!isOpen) {
      reportedOffer.current = false;
      return;
    }
    if (reportedOffer.current || !upsells.length) return;
    reportedOffer.current = true;
    reportOfferShown(upsells, CART_DRAWER);
  }, [isOpen, upsells]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [isOpen, onClose]);

  if (!mounted) return null;

  /* The subscription's bundled gift is chosen on the checkout page, so the
     drawer does not list it — showing it here offers a choice this screen
     cannot make, and removing it is not the shopper's to do. */
  const lines = visibleLines(totals.lines);
  const subLine = lines.find((line) => line.kind === 'sub');
  const otherLines = lines.filter((line) => line.kind !== 'sub');

  return (
    <div className={`coProto-drawer ${shown ? 'is-shown' : ''}`}>
      <button
        type='button'
        className='coProto-drawer__scrim'
        onClick={onClose}
        aria-label='Close cart'
      />
      <div
        className='coProto-drawer__panel'
        role='dialog'
        aria-modal='true'
        aria-label='Your cart'
      >
        <div className='coProto-drawer__head'>
          <span className='coProto-drawer__title'>Your Cart</span>
          <button type='button' onClick={onClose} aria-label='Close cart'>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Under the head so it does not scroll away with the items, and only
            with something in the cart: an empty one is $49 short of free
            shipping by the arithmetic, which is not news the shopper can act
            on. Closing the drawer is what the link has to do as well as
            navigate — nothing else dismisses it on a route change. */}
        {lines.length ? (
          <FreeShippingNudge
            gap={totals.freeShippingGap}
            onNavigate={onClose}
          />
        ) : null}

        <div className='coProto-drawer__items'>
          {subLine ? (
            <Item
              line={subLine}
              disabled={isBusy}
              onRemove={onRemoveLine}
              onQty={onChangeQty}
            />
          ) : null}

          {otherLines.map((line) => (
            <Item
              key={line.id}
              line={line}
              disabled={isBusy}
              onRemove={onRemoveLine}
              onQty={onChangeQty}
            />
          ))}

          {isAwaitingCart ? <Skeleton /> : null}

          {!lines.length && !isAwaitingCart ? (
            <div className='coProto-drawer__empty'>
              <h2>Your cart is empty</h2>
              <p>Add something and it will show up here.</p>
            </div>
          ) : null}
        </div>

        {/* Below the cart and above the total: it is an addition to the order
            rather than part of it, and nothing here should read as something
            the shopper already chose. An empty cart is offered nothing —
            there is no order yet to add to. */}
        {lines.length && upsells.length ? (
          <Upsells
            upsells={upsells}
            disabled={isBusy}
            addingUpsellId={addingUpsellId}
            onAdd={onAddUpsell}
          />
        ) : null}

        <div className='coProto-drawer__foot'>
          <div className='coProto-drawer__row'>
            <span>Subtotal</span>
            {/* Discounts already taken off: this is the last number before the
                Checkout button, with no Total under it to correct a
                pre-discount one. A total of $0.00 is a number the shopper can
                act on, and until the cart is read there isn't one. */}
            <span>
              {isAwaitingCart ? '—' : money(totals.subtotalAfterDiscount || 0)}
            </span>
          </div>
          <Button
            variant='checkout'
            fullWidth
            className='coProto-ctaArrow'
            onClick={onCheckout}
            disabled={!lines.length || isBusy}
          >
            Checkout
            <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
